import { EventTimeline, MatrixEvent, Room } from 'matrix-js-sdk/src/matrix';

import logger from 'utils/logging/faro';
import { CustomMeedioMatrixEvents, isSystemUser, loadJoinedMembers } from 'utils/matrixUtils';

const parseManagerIdsFromContent = (
  content: { managers?: Record<string, unknown> | string[] } | undefined
): Set<string> => {
  const managers = content?.managers;

  if (Array.isArray(managers)) return new Set(managers.filter((id): id is string => typeof id === 'string'));
  if (managers && typeof managers === 'object') return new Set(Object.keys(managers));

  return new Set();
};

const getLatestRoomManagersEvent = (room: Room): MatrixEvent | null => {
  const state = room.getLiveTimeline().getState(EventTimeline.FORWARDS);
  const stateEvent = state?.getStateEvents(CustomMeedioMatrixEvents.ROOM_MANAGERS, '') ?? null;

  const timelineEvents = room.getLiveTimeline().getEvents();
  let latestTimelineEvent: MatrixEvent | null = null;
  for (let i = timelineEvents.length - 1; i >= 0; i--) {
    const ev = timelineEvents[i];
    if (ev.getType() === CustomMeedioMatrixEvents.ROOM_MANAGERS && ev.getStateKey() === '') {
      latestTimelineEvent = ev;
      break;
    }
  }

  if (!stateEvent) return latestTimelineEvent;
  if (!latestTimelineEvent) return stateEvent;

  return latestTimelineEvent.getTs() >= stateEvent.getTs() ? latestTimelineEvent : stateEvent;
};

/** V2 map `{ managers: { [mxid]: { reason } } }` or legacy `{ managers: string[] }`. */
export const readRoomManagerIds = (room: Room): Set<string> | null => {
  const event = getLatestRoomManagersEvent(room);
  if (!event) return null;

  return parseManagerIdsFromContent(event.getContent() as { managers?: Record<string, unknown> | string[] });
};

/**
 * MIP F-15b: sole manager with other human members must pick a successor before leaving.
 * Async because, under `lazyLoadMembers: true`, the joined member list must be loaded before the
 * "other human members?" guard can be evaluated reliably. The cheap guards run first so no `/members`
 * request is made when the answer is already known.
 *
 * Personal-workspace chats are created without an `eu.meedio.room.managers` event (no workspace ⇒ no
 * managers state, no bot, no power-level overrides). For those rooms `readRoomManagerIds` returns
 * `null`, so we fall back to Matrix power levels: prompt when the current user is the unique
 * highest-power-level human and at least one other human member remains. Workspace chats always carry
 * the managers event, so the fallback branch never runs for them.
 */
export const shouldPromptRoomManagerSuccessor = async (
  room: Room,
  currentUserId: string | null | undefined
): Promise<boolean> => {
  if (!currentUserId) return false;

  const managerIds = readRoomManagerIds(room);

  if (managerIds === null) {
    let members;
    try {
      members = await loadJoinedMembers(room);
    } catch (error) {
      logger.error('Failed to load room members for power-level successor check', {
        roomId: room.roomId,
        error,
      });
      return false;
    }

    const otherHumanMembers = members.filter(({ userId }) => userId !== currentUserId);
    if (otherHumanMembers.length === 0) return false;

    const currentUserPl = room.getMember(currentUserId)?.powerLevel ?? 0;
    return otherHumanMembers.every(({ userId }) => (room.getMember(userId)?.powerLevel ?? 0) < currentUserPl);
  }

  const isSoleManager = managerIds.size === 1 && managerIds.has(currentUserId);
  if (!isSoleManager) return false;

  let members;
  try {
    members = await loadJoinedMembers(room);
  } catch (error) {
    logger.error('Failed to load room members before successor check', { roomId: room.roomId, error });
    members = room.getJoinedMembers().filter(({ userId }) => !isSystemUser(userId));
  }

  return members.some(({ userId }) => userId !== currentUserId);
};
